import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import TrendingResultPage from './TrendingResultPage'
import { searchTrendingTopics } from './trendingUtils'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { AGENT_TYPES } from '../index.js'
import { TrendingUp, Calendar, Search, Clock, Globe } from 'lucide-react'

// Trending Agent States
const AGENT_STATES = {
  INPUT: 'input',
  SEARCHING: 'searching',
  COMPLETED: 'completed'
}

const TrendingAgent = ({ query, onBack, llmService, options = {} }) => {
  const [currentState, setCurrentState] = useState(AGENT_STATES.INPUT)
  const [trendingResult, setTrendingResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()
  
  // Form state - initialize with options from homepage with proper default dates
  const getDefaultFromDate = () => {
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1) // Default to yesterday
    return yesterday.toISOString().split('T')[0]
  }
  
  const getDefaultToDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0] // Default to today
  }
  
  const [searchQuery, setSearchQuery] = useState(query || '')
  const [fromDate, setFromDate] = useState(query ? (options.fromDate || getDefaultFromDate()) : getDefaultFromDate())
  const [toDate, setToDate] = useState(query ? (options.toDate || getDefaultToDate()) : getDefaultToDate())
  const [selectedRegion, setSelectedRegion] = useState(options.region || 'Indonesia')
  
  // Calculate minimum dates and handle date validation
  const getMinFromDate = () => {
    const maxDaysBack = new Date()
    maxDaysBack.setDate(maxDaysBack.getDate() - 30) // Allow up to 30 days back
    return maxDaysBack.toISOString().split('T')[0]
  }
  
  const getMaxFromDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0] // Allow up to today
  }
  
  const getMinToDate = () => {
    const selectedFromDate = new Date(fromDate)
    const dayAfterFrom = new Date(selectedFromDate)
    dayAfterFrom.setDate(selectedFromDate.getDate() + 1)
    return dayAfterFrom.toISOString().split('T')[0]
  }
  
  const getMaxToDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0] // Allow up to today
  }
  
  // Handle date changes with validation
  const handleFromDateChange = (newFromDate) => {
    setFromDate(newFromDate)
    
    // If toDate is same or before fromDate, adjust toDate
    const fromDateObj = new Date(newFromDate)
    const toDateObj = new Date(toDate)
    
    if (toDateObj <= fromDateObj) {
      const nextDay = new Date(fromDateObj)
      nextDay.setDate(fromDateObj.getDate() + 1)
      
      const maxToDate = new Date() // Today
      
      // Set toDate to next day or today, whichever is earlier
      const newToDate = nextDay <= maxToDate ? nextDay : maxToDate
      setToDate(newToDate.toISOString().split('T')[0])
    }
  }
  
  const handleToDateChange = (newToDate) => {
    setToDate(newToDate)
    
    // If fromDate is same or after toDate, adjust fromDate
    const fromDateObj = new Date(fromDate)
    const toDateObj = new Date(newToDate)
    
    if (fromDateObj >= toDateObj) {
      const prevDay = new Date(toDateObj)
      prevDay.setDate(toDateObj.getDate() - 1)
      
      const minFromDate = new Date()
      minFromDate.setDate(minFromDate.getDate() - 30)
      
      // Set fromDate to previous day or min date, whichever is later
      const newFromDate = prevDay >= minFromDate ? prevDay : minFromDate
      setFromDate(newFromDate.toISOString().split('T')[0])
    }
  }
  
  // Use refs to track state and prevent unnecessary re-search
  const isSearching = useRef(false)
  const hasEverSearched = useRef(false)
  const hasConsumedCredits = useRef(false)

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `trending_agent_${searchQuery}_${fromDate}_${toDate}_${suffix}`
  const getCreditKey = () => `trending_agent_credits_${searchQuery}_${fromDate}_${toDate}`

  // Load persisted state on component mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persistedState = sessionStorage.getItem(getSessionKey('state'))
        const persistedResult = sessionStorage.getItem(getSessionKey('result'))
        
        if (persistedState && persistedResult) {
          const state = JSON.parse(persistedState)
          const resultData = JSON.parse(persistedResult)
          
          console.log('📋 Loading persisted trending state:', state.currentState)
          setCurrentState(state.currentState)
          setTrendingResult(resultData)
          setHasInitialized(true)
          hasEverSearched.current = true
          
          return true
        }
      } catch (error) {
        console.warn('⚠️ Failed to load persisted state:', error)
        try {
          sessionStorage.removeItem(getSessionKey('state'))
          sessionStorage.removeItem(getSessionKey('result'))
        } catch (clearError) {
          console.warn('⚠️ Failed to clear corrupted session data:', clearError)
        }
      }
      return false
    }
    
    // Load persisted state if available
    const hasPersistedState = loadPersistedState()
    if (!hasPersistedState && query) {
      setSearchQuery(query)
      // Auto-start search immediately if query is provided from homepage
      setCurrentState(AGENT_STATES.SEARCHING)
    }
  }, [])

  // Auto-start search when query is provided from homepage or state changes to SEARCHING
  useEffect(() => {
    if (currentState === AGENT_STATES.SEARCHING && searchQuery && !isSearching.current) {
      handleStartSearch()
    }
  }, [currentState, searchQuery])

  // Persist state changes to session storage
  useEffect(() => {
    if (hasInitialized && trendingResult) {
      try {
        sessionStorage.setItem(getSessionKey('state'), JSON.stringify({
          currentState,
          hasInitialized: true
        }))
        sessionStorage.setItem(getSessionKey('result'), JSON.stringify(trendingResult))
      } catch (error) {
        console.warn('⚠️ Failed to persist state:', error)
      }
    }
  }, [currentState, trendingResult, hasInitialized, searchQuery, fromDate, toDate])

  const handleStartSearch = async () => {
    if (isSearching.current || !searchQuery.trim()) {
      return
    }
    
    // Check credits before starting search (2 credits for trending agent)
    const canProceed = await checkAndWarnCredits(AGENT_TYPES.TRENDING, 2)
    if (!canProceed) {
      return
    }

    isSearching.current = true
    setCurrentState(AGENT_STATES.SEARCHING)
    setIsLoading(true)
    setError(null)
    setTrendingResult(null)
    
    try {
      console.log(`🔥 Starting trending search for: ${searchQuery} from ${fromDate} to ${toDate}`)
      
      // Create date range for the selected dates
      const fromDateTime = new Date(fromDate + 'T00:00:00+07:00').toISOString()
      const toDateTime = new Date(toDate + 'T23:59:59+07:00').toISOString()
      
      // Search trending topics with LLM enhancement
      const result = await searchTrendingTopics(
        searchQuery,
        fromDateTime,
        toDateTime,
        selectedRegion,
        llmService
      )
      
      console.log('✅ Trending search completed:', result)
      
      // Consume credits after successful search
      if (!hasConsumedCredits.current) {
        try {
          await consumeCredits(2, AGENT_TYPES.TRENDING, {
            query: searchQuery,
            fromDate: fromDate,
            toDate: toDate,
            region: selectedRegion
          })
          hasConsumedCredits.current = true
          sessionStorage.setItem(getCreditKey(), 'consumed')
          console.log('💳 Credits consumed for trending search')
        } catch (creditError) {
          console.warn('⚠️ Failed to consume credits:', creditError)
        }
      }
      
      const finalResult = {
        ...result,
        dateRange: { fromDate: fromDateTime, toDate: toDateTime }
      }
      
      setTrendingResult(finalResult)
      setCurrentState(AGENT_STATES.COMPLETED)
      setHasInitialized(true)
      hasEverSearched.current = true
      
      // Save result to agent history
      try {
        await AgentHistoryIntegration.saveTrendingResult({
          query: searchQuery,
          ...finalResult
        })
        console.log('✅ Trending result saved to history')
      } catch (historyError) {
        console.warn('⚠️ Failed to save trending result to history:', historyError)
      }
      
    } catch (searchError) {
      console.error('❌ Error in trending search:', searchError)
      setError(searchError.message || 'Terjadi kesalahan saat mencari trending topics')
    } finally {
      setIsLoading(false)
      isSearching.current = false
    }
  }

  const handleRetry = () => {
    // Clear session data for retry
    try {
      sessionStorage.removeItem(getSessionKey('state'))
      sessionStorage.removeItem(getSessionKey('result'))
      sessionStorage.removeItem(getCreditKey())
    } catch (error) {
      console.warn('⚠️ Failed to clear session data:', error)
    }
    
    // Reset state
    setCurrentState(AGENT_STATES.INPUT)
    setTrendingResult(null)
    setError(null)
    setHasInitialized(false)
    hasEverSearched.current = false
    isSearching.current = false
    hasConsumedCredits.current = false
  }



  // Render based on current state
  if (currentState === AGENT_STATES.COMPLETED && trendingResult) {
    return (
      <TrendingResultPage
        query={searchQuery}
        trendingResult={trendingResult}
        dateRange={trendingResult.dateRange}
        onBack={handleRetry}
        onRetry={handleRetry}
      />
    )
  }

  // Show loading or error state when query is provided from homepage
  if (query && (isLoading || error)) {
    return (
      <div className="min-h-screen bg-bg-primary">
        {/* Header */}
        <div className="bg-bg-secondary border-b border-border-primary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <button
                  onClick={onBack}
                  className="flex items-center space-x-2 text-text-tertiary hover:text-text-primary transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="font-medium">Kembali</span>
                </button>
                <div className="h-6 w-px bg-border-primary" />
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-6 w-6 text-orange-500" />
                  <h1 className="text-xl font-bold text-text-primary">Trending Agent</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
           {/* Loading State */}
           {isLoading && (
             <div className="bg-bg-tertiary rounded-xl shadow-sm border border-border-primary p-8">
               <div className="text-center">
                 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4" />
                 <h3 className="text-lg font-semibold text-text-primary mb-2">Mencari Trending Topics...</h3>
                 <p className="text-text-secondary">
                   Sedang menganalisis trend terbaru untuk "{searchQuery}" dari {new Date(fromDate).toLocaleDateString('id-ID')} sampai {new Date(toDate).toLocaleDateString('id-ID')}
                 </p>
               </div>
             </div>
           )}
           
           {/* Error State */}
           {error && (
             <div className="bg-status-error/20 border border-status-error rounded-xl p-6">
               <div className="flex items-center space-x-3">
                 <div className="bg-status-error/20 p-2 rounded-full">
                   <svg className="h-5 w-5 text-status-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                   </svg>
                 </div>
                 <div>
                   <h3 className="text-lg font-semibold text-text-primary">Terjadi Kesalahan</h3>
                   <p className="text-text-secondary">{error}</p>
                 </div>
               </div>
               <button
                 onClick={handleRetry}
                 className="mt-4 px-4 py-2 bg-status-error text-text-primary rounded-lg hover:bg-status-error/80 transition-colors"
               >
                 Coba Lagi
               </button>
             </div>
           )}
         </div>
      </div>
    )
  }

  // Only show input form if no query is provided (direct access to trending agent)
  if (!query) {
    return (
      <div className="min-h-screen bg-bg-primary">
      {/* Header */}
      <div className="bg-bg-secondary border-b border-border-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-text-tertiary hover:text-text-primary transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium">Kembali</span>
              </button>
              <div className="h-6 w-px bg-border-primary" />
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-6 w-6 text-orange-500" />
                <h1 className="text-xl font-bold text-text-primary">Trending Agent</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-orange-500/20 p-3 rounded-full">
              <TrendingUp className="h-8 w-8 text-orange-500" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-text-primary mb-2">
            Deteksi Trend Terbaru
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Temukan topik yang sedang trending di internet dengan filter tanggal dan kategori yang spesifik
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-bg-tertiary rounded-xl shadow-sm border border-border-primary p-6 mb-8">
          <div className="space-y-6">
            {/* Date Range Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* From Date */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Dari Tanggal
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => handleFromDateChange(e.target.value)}
                  min={getMinFromDate()}
                  max={toDate ? new Date(new Date(toDate).getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0] : getMaxFromDate()}
                  className="w-full px-4 py-3 bg-bg-quaternary border border-border-primary text-text-primary rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                />
              </div>

              {/* To Date */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Sampai Tanggal
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => handleToDateChange(e.target.value)}
                  min={fromDate ? new Date(new Date(fromDate).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] : getMinToDate()}
                  max={getMaxToDate()}
                  className="w-full px-4 py-3 bg-bg-quaternary border border-border-primary text-text-primary rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                />
              </div>
            </div>

            {/* Region Selection */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                <Globe className="h-4 w-4 inline mr-1" />
                Negara
              </label>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="w-full px-4 py-3 bg-bg-quaternary border border-border-primary text-text-primary rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              >
                <option value="Indonesia">🇮🇩 Indonesia</option>
                <option value="United States">🇺🇸 United States</option>
                <option value="China">🇨🇳 China</option>
                <option value="Japan">🇯🇵 Japan</option>
                <option value="Germany">🇩🇪 Germany</option>
                <option value="India">🇮🇳 India</option>
                <option value="United Kingdom">🇬🇧 United Kingdom</option>
                <option value="France">🇫🇷 France</option>
                <option value="Italy">🇮🇹 Italy</option>
                <option value="Brazil">🇧🇷 Brazil</option>
                <option value="Canada">🇨🇦 Canada</option>
                <option value="Russia">🇷🇺 Russia</option>
                <option value="South Korea">🇰🇷 South Korea</option>
                <option value="Australia">🇦🇺 Australia</option>
                <option value="Spain">🇪🇸 Spain</option>
                <option value="Mexico">🇲🇽 Mexico</option>
                <option value="Turkey">🇹🇷 Turkey</option>
                <option value="Netherlands">🇳🇱 Netherlands</option>
                <option value="Saudi Arabia">🇸🇦 Saudi Arabia</option>
                <option value="Switzerland">🇨🇭 Switzerland</option>
                <option value="Taiwan">🇹🇼 Taiwan</option>
                <option value="Belgium">🇧🇪 Belgium</option>
                <option value="Argentina">🇦🇷 Argentina</option>
                <option value="Ireland">🇮🇪 Ireland</option>
                <option value="Israel">🇮🇱 Israel</option>
                <option value="Austria">🇦🇹 Austria</option>
                <option value="Nigeria">🇳🇬 Nigeria</option>
                <option value="Norway">🇳🇴 Norway</option>
                <option value="United Arab Emirates">🇦🇪 United Arab Emirates</option>
                <option value="Egypt">🇪🇬 Egypt</option>
                <option value="South Africa">🇿🇦 South Africa</option>
                <option value="Philippines">🇵🇭 Philippines</option>
                <option value="Bangladesh">🇧🇩 Bangladesh</option>
                <option value="Chile">🇨🇱 Chile</option>
                <option value="Finland">🇫🇮 Finland</option>
                <option value="Romania">🇷🇴 Romania</option>
                <option value="Czech Republic">🇨🇿 Czech Republic</option>
                <option value="New Zealand">🇳🇿 New Zealand</option>
                <option value="Peru">🇵🇪 Peru</option>
                <option value="Greece">🇬🇷 Greece</option>
                <option value="Portugal">🇵🇹 Portugal</option>
                <option value="Vietnam">🇻🇳 Vietnam</option>
                <option value="Malaysia">🇲🇾 Malaysia</option>
                <option value="Thailand">🇹🇭 Thailand</option>
                <option value="Singapore">🇸🇬 Singapore</option>
                <option value="Denmark">🇩🇰 Denmark</option>
                <option value="Sweden">🇸🇪 Sweden</option>
                <option value="Poland">🇵🇱 Poland</option>
                <option value="Ukraine">🇺🇦 Ukraine</option>
                <option value="Morocco">🇲🇦 Morocco</option>
                <option value="Kenya">🇰🇪 Kenya</option>
              </select>
            </div>

            {/* Search Button */}
            <div className="flex justify-center">
              <button
                onClick={handleStartSearch}
                disabled={isLoading || !searchQuery.trim()}
                className="flex items-center space-x-2 px-8 py-3 bg-brand-primary text-text-primary rounded-lg hover:bg-brand-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-text-primary" />
                    <span>Mencari Trending...</span>
                  </>
                ) : (
                  <>
                    <Search className="h-5 w-5" />
                    <span>Cari Trending Topics</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>





        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-bg-tertiary rounded-xl shadow-sm border border-border-primary p-6">
            <div className="flex items-center space-x-3 mb-3">
              <div className="bg-orange-500/20 p-2 rounded-lg">
                <TrendingUp className="h-5 w-5 text-orange-500" />
              </div>
              <h3 className="font-semibold text-text-primary">Real-time Trending</h3>
            </div>
            <p className="text-sm text-text-secondary">
              Dapatkan informasi trending terbaru dari berbagai sumber terpercaya dengan filter tanggal yang akurat
            </p>
          </div>

          <div className="bg-bg-tertiary rounded-xl shadow-sm border border-border-primary p-6">
            <div className="flex items-center space-x-3 mb-3">
              <div className="bg-blue-500/20 p-2 rounded-lg">
                <Clock className="h-5 w-5 text-blue-400" />
              </div>
              <h3 className="font-semibold text-text-primary">Filter Tanggal</h3>
            </div>
            <p className="text-sm text-text-secondary">
              Pilih tanggal spesifik untuk melihat apa yang trending pada hari tertentu
            </p>
          </div>

          <div className="bg-bg-tertiary rounded-xl shadow-sm border border-border-primary p-6">
            <div className="flex items-center space-x-3 mb-3">
              <div className="bg-green-500/20 p-2 rounded-lg">
                <Globe className="h-5 w-5 text-green-400" />
              </div>
              <h3 className="font-semibold text-text-primary">Multi Region</h3>
            </div>
            <p className="text-sm text-text-secondary">
              Pilih antara sumber Indonesia atau International untuk perspektif yang berbeda
            </p>
          </div>
        </div>
      </div>

      {/* Credit Warning Modal */}
      {warningModal.show && (
        <CreditWarningModal
          isOpen={warningModal.show}
          onClose={closeWarning}
          requiredCredits={warningModal.requiredCredits}
          currentCredits={warningModal.currentCredits}
          agentType={warningModal.agentType}
        />
      )}
    </div>
    )
  }
}

TrendingAgent.propTypes = {
  query: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object,
  options: PropTypes.object
}

export default TrendingAgent